/**
 * Native ATD ("approved to dispense") on the Scheduling side: the queue of
 * residents waiting to be dispensed, grouped by building, with where each one
 * stands on the schedule (GET /scheduling/atd), the plan command
 * (POST /scheduling/atd/:patientId/plan) and the dispense command
 * (POST /atd/residents/:patientId). The ATD status itself is Finance's board;
 * Scheduling records only where the resident will be seen.
 */
import api from '../../services/api';
import type { ApiResult, SchedulingScope } from '../finance/financeApi';

export type AtdPlanState = 'NOT_PLANNED' | 'ON_MANIFEST' | 'PIT_STOP';
/**
 * Why a recorded plan no longer holds, judged by the server against the
 * resident's CURRENT building and today. VISIT_OTHER_BUILDING (the resident
 * moved) arrives with state NOT_PLANNED: the plan was for another building.
 * VISIT_COMPLETED is kept for an older server; the current one says VISIT_PASSED.
 */
export type AtdAttention = 'VISIT_OTHER_BUILDING' | 'VISIT_CANCELLED' | 'VISIT_COMPLETED' | 'VISIT_PASSED' | 'PIT_STOP_PASSED';

export const ATD_ATTENTION_LABEL: Record<AtdAttention, string> = {
  VISIT_OTHER_BUILDING: 'Planned on another building\'s visit (the resident moved): plan again here',
  VISIT_CANCELLED: 'The visit was cancelled',
  VISIT_COMPLETED: 'The visit completed without a dispense recorded',
  VISIT_PASSED: 'The visit date passed without a dispense recorded',
  PIT_STOP_PASSED: 'The pit stop date passed',
};

export interface AtdSchedule {
  state: AtdPlanState;
  visit_plan_id: string | null;
  visit_on: string | null;
  visit_state: string | null;
  pit_stop_on: string | null;
  note: string | null;
  recorded_at: string | null;
  recorded_by: string | null;
  /** What Scheduling recorded; `state` is how it reads now (a plan on another building's visit reads NOT_PLANNED). */
  recorded_state?: AtdPlanState;
  attention: AtdAttention | null;
  on_next_visit?: boolean;
}

export interface AtdResident {
  patient_id: string;
  name: string;
  room: string | null;
  /** CounselEAR's Preferred Clinic, sent when eMerge has no building for the resident (2026-10-14). */
  counselear_clinic_id?: string | null;
  counselear_clinic_name?: string | null;
  atd_date: string | null;
  days_waiting: number | null;
  atd_event_id: string | null;
  schedule: AtdSchedule;
}

export interface AtdOpenVisit { id: string; planned_on: string; state: 'PLANNED' | 'CONFIRMED'; provider: string | null }

export interface AtdBuilding {
  /** null = "No building on file", listed last. */
  facility: { id: string; name: string } | null;
  next_visit: { on: string; source: 'VISIT_PLAN' | 'SCHEDULING'; state: string | null } | null;
  provider: string | null;
  open_visits: AtdOpenVisit[];
  /** The building is closed for scheduling (2026-10-14): the server refuses ON_MANIFEST here. Absent from an older server. */
  closure?: { kind: 'DROPPED' | 'DO_NOT_SCHEDULE' | null; reason: string | null } | null;
  residents: AtdResident[];
  /** What this building's rows offer: the actions' own scopes, never more than the queue-wide `can`. Absent from an older server. */
  can?: { plan: boolean; dispense: boolean };
}

export interface AtdQueueData {
  as_of: string;
  counts: { total: number; NOT_PLANNED: number; ON_MANIFEST: number; PIT_STOP: number; attention: number };
  buildings: AtdBuilding[];
  plan_states: AtdPlanState[];
  can: { plan: boolean; dispense: boolean };
}

export interface AtdPlanBody { state: AtdPlanState; visit_plan_id?: string; pit_stop_on?: string; note?: string }

/** One per submit: a retry of the same submit re-sends the same key. */
export const newAtdKey = (): string => {
  const raw = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `atd-${raw}`;
};

const withKey =(key: string) => ({ headers: { 'Idempotency-Key': key } });

/** The ATD fields the scope carries; absent before the deploy and false/0 before the migration. */
export function atdScope(scope: SchedulingScope): { ready: number; notPlanned: number; available: boolean } {
  const c = (scope.counts || {}) as Record<string, unknown>;
  return {
    ready: typeof c.atd_ready === 'number' ? c.atd_ready : 0,
    notPlanned: typeof c.atd_not_planned === 'number' ? c.atd_not_planned : 0,
    available: c.atd_available !== false,
  };
}

export const atdApi = {
  queue: async (): Promise<ApiResult<AtdQueueData>> => (await api.get('/scheduling/atd')) as ApiResult<AtdQueueData>,
  plan: async (patientId: string, body: AtdPlanBody, key: string): Promise<ApiResult<{ changed?: boolean; unchanged?: boolean }>> =>
    (await api.post(`/scheduling/atd/${encodeURIComponent(patientId)}/plan`, body, withKey(key))) as ApiResult<{ changed?: boolean; unchanged?: boolean }>,
  dispense: async (patientId: string, effectiveDate: string, key: string): Promise<ApiResult<{ changed: boolean; status: string }>> =>
    (await api.post(`/atd/residents/${encodeURIComponent(patientId)}`, { to_status: 'Dispensed', effective_date: effectiveDate || undefined }, withKey(key))) as ApiResult<{ changed: boolean; status: string }>,
};
